import { useEffect } from "react";

export const useContentProtection = (): void => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      // event.key can be undefined for some synthetic/IME/autofill events.
      const key = event.key?.toLowerCase();
      if (!key) {
        return;
      }

      // Block Ctrl+S / Cmd+S (save page).
      if ((event.ctrlKey || event.metaKey) && key === "s") {
        event.preventDefault();
        return;
      }

    
      const isPrintScreen = key === "printscreen";
      const isSnipShortcut =
        (event.metaKey || event.getModifierState?.("Meta")) &&
        event.shiftKey &&
        (key === "s" || key === "3" || key === "4" || key === "5");

      if (isPrintScreen || isSnipShortcut) {
        event.preventDefault();
      }
    };

    // Some browsers only surface PrtScn on keyup. Also, on Windows a plain
    // PrtScn copies the screen to the clipboard, so we defensively clear it.
    const handleKeyUp = (event: KeyboardEvent): void => {
      if (event.key?.toLowerCase() === "printscreen") {
        event.preventDefault();
        if (navigator.clipboard?.writeText) {
          navigator.clipboard.writeText("").catch(() => {
            /* clipboard write can reject without focus/permission — ignore */
          });
        }
      }
    };

    const handleContextMenu = (event: MouseEvent): void => {
      event.preventDefault();
    };

    const handleDragStart = (event: DragEvent): void => {
      const target = event.target as HTMLElement | null;
      if (target && target.tagName === "IMG") {
        event.preventDefault();
      }
    };

    // Set draggable="false" on every image, current and future. A single
    // dragstart listener already blocks the drag; the attribute is added per
    // the content-protection requirement and covers images injected later
    // (preload/reveal, route changes) without editing each <img> by hand.
    const markImages = (): void => {
      document
        .querySelectorAll<HTMLImageElement>("img:not([draggable='false'])")
        .forEach((img) => {
          img.setAttribute("draggable", "false");
        });
    };

    markImages();

    const observer = new MutationObserver(markImages);
    observer.observe(document.body, { childList: true, subtree: true });

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("dragstart", handleDragStart);

    return () => {
      observer.disconnect();
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("dragstart", handleDragStart);
    };
  }, []);
};
